import { DragEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, Deal, DealList, fmtDay, money, Pipeline } from '../api';
import { useSession } from '../session';
import { ErrorBox, useAsync } from '../components/ui';
import { requiredHint, useMoveDeal } from '../components/MoveDeal';
import { DealForm, DealInput } from './DealForm';

/** Értékesítési tábla: oszlop = lépés; a kártya húzható, vagy a kártyán lévő menüvel léptethető (mobilon is). */
export function PipelinePage() {
  const s = useSession();
  const nav = useNavigate();
  const [pipes, setPipes] = useState<Pipeline[] | null>(null);
  const [lostReasons, setLostReasons] = useState<string[]>([]);
  const [pid, setPid] = useState<string>('');
  const [mine, setMine] = useState(false);
  const [staleOnly, setStaleOnly] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [q, setQ] = useState('');
  const [data, setData] = useState<DealList | null>(null);
  const [creating, setCreating] = useState(false);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const load = useAsync();
  const create = useAsync();

  useEffect(() => {
    load.run(async () => {
      const r = await api<{ items: Pipeline[]; lost_reasons: string[] }>('/pipelines');
      setPipes(r.items); setLostReasons(r.lost_reasons);
      setPid((cur) => cur && r.items.some((p) => p.id === cur) ? cur : (r.items.find((p) => p.is_default) ?? r.items[0])?.id ?? '');
    });
  }, []);
  const reload = () => {
    if (!pid) return;
    const qs = new URLSearchParams({ pipeline_id: pid, limit: '500' });
    if (!showClosed) qs.set('status', 'open');
    if (mine) qs.set('owner', 'me');
    if (staleOnly) qs.set('stale', 'true');
    if (q.trim()) qs.set('query', q.trim());
    return load.run(async () => setData(await api<DealList>(`/deals?${qs}`)));
  };
  useEffect(() => { const t = setTimeout(reload, q ? 200 : 0); return () => clearTimeout(t); }, [pid, mine, staleOnly, showClosed, q]);

  const { move, dialog, error: moveError } = useMoveDeal(lostReasons, () => reload());
  const pipe = pipes?.find((p) => p.id === pid);
  const columns = useMemo(() => (pipe?.stages ?? []).filter((st) => showClosed || st.kind === 'open'), [pipe, showClosed]);
  const byStage = useMemo(() => {
    const m = new Map<string, Deal[]>();
    for (const d of data?.items ?? []) m.set(d.stage_id, [...(m.get(d.stage_id) ?? []), d]);
    return m;
  }, [data]);

  const submit = async (v: DealInput) => {
    const d = await create.run(() => api<Deal>('/deals', { method: 'POST', body: v }));
    if (d) nav(`/ertekesites/${d.id}`);
  };
  const onDrop = (e: DragEvent, stageId: string) => {
    e.preventDefault(); setDragOver(null);
    const id = e.dataTransfer.getData('text/deal');
    const d = data?.items.find((x) => x.id === id);
    const st = pipe?.stages.find((x) => x.id === stageId);
    if (d && st) move(d, st);
  };

  return (
    <div>
      <div className="page-head">
        <h2>Értékesítés</h2>
        {pipes && pipes.length > 1 && (
          <select aria-label="Pipeline" value={pid} onChange={(e) => setPid(e.target.value)} style={{ width: 'auto' }}>
            {pipes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <input className="search" placeholder="Keresés deal vagy cég szerint…" value={q} onChange={(e) => setQ(e.target.value)} />
        <button onClick={() => setCreating(true)}>Új deal</button>
        {(s.role === 'owner' || s.role === 'admin') && <Link to="/ertekesites/beallitasok">Lépések beállítása</Link>}
      </div>
      <div className="filters">
        <label className="check"><input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /> csak az enyéim</label>
        <label className="check"><input type="checkbox" checked={staleOnly} onChange={(e) => setStaleOnly(e.target.checked)} /> csak az elakadtak</label>
        <label className="check"><input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} /> lezártak is</label>
        <span className="spacer" />
        {data?.totals.map((t) => (
          <span key={t.currency} className="muted">{t.count} deal · {money(t.value, t.currency)} · súlyozva {money(t.weighted, t.currency)}</span>
        ))}
      </div>
      {creating && (
        <section className="card">
          <h3>Új deal</h3>
          <DealForm pipelines={pipes ?? []} onSubmit={submit} onCancel={() => { setCreating(false); create.setError(null); }} error={create.error} busy={create.busy} />
        </section>
      )}
      <ErrorBox error={load.error} />
      {moveError}
      {dialog}
      <div className="board" style={{ gridTemplateColumns: `repeat(${Math.max(columns.length, 1)}, minmax(220px, 1fr))` }}>
        {columns.map((st) => {
          const deals = byStage.get(st.id) ?? [];
          const sum = deals.reduce((acc, d) => acc + (d.currency === 'HUF' ? d.value ?? 0 : 0), 0);
          return (
            <section key={st.id} className={`col col-${st.kind} ${dragOver === st.id ? 'over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(st.id); }} onDragLeave={() => setDragOver(null)} onDrop={(e) => onDrop(e, st.id)}>
              <header title={requiredHint(st)}>
                <strong>{st.name}</strong> <span className="muted">{st.probability}% · {deals.length}</span>
                {sum > 0 && <div className="muted small">{money(sum)}</div>}
              </header>
              {deals.map((d) => (
                <article key={d.id} className={`deal-card ${d.is_stale ? 'stale' : ''}`} draggable
                  onDragStart={(e) => e.dataTransfer.setData('text/deal', d.id)}>
                  <Link to={`/ertekesites/${d.id}`} className="deal-title">{d.title}</Link>
                  {d.company_name && <div className="muted small">{d.company_name}</div>}
                  <div className="deal-meta">
                    <span>{money(d.value, d.currency)}</span>
                    {d.expected_close && <span className="muted small">zárás: {fmtDay(d.expected_close)}</span>}
                  </div>
                  {d.is_stale && <div className="stale-note">Elakadt: {d.days_in_stage} napja ebben a lépésben</div>}
                  {d.status === 'open' && (d.next_task
                    ? <div className="small">→ {d.next_task.title}{d.next_task.due_date && <span className="muted"> ({fmtDay(d.next_task.due_date)})</span>}</div>
                    : <div className="small muted">Nincs következő lépés</div>)}
                  {d.status === 'lost' && d.lost_reason && <div className="small muted">Ok: {d.lost_reason}</div>}
                  <select className="move" aria-label={`${d.title} áthelyezése`} value={d.stage_id}
                    onChange={(e) => { const t = pipe!.stages.find((x) => x.id === e.target.value); if (t) move(d, t); }}>
                    {pipe!.stages.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                  </select>
                </article>
              ))}
              {deals.length === 0 && <p className="muted small empty">Húzz ide egy dealt.</p>}
            </section>
          );
        })}
      </div>
      {data && data.total > data.items.length && <p className="muted">Az első {data.items.length} deal látszik ({data.total} van). Szűkíts a keresővel.</p>}
    </div>
  );
}
